// src/portals/patient/pages/profile/OtherNamesCard.tsx

/**
 * Purpose : "Other names" on the profile: every name the patient confirmed as
 *           theirs by answering "Yes, this is me" on an upload (a second last
 *           name, a married or maiden name, a middle initial). Later uploads
 *           with one of these names and their exact birth date are added
 *           without asking — so the patient sees exactly what is recognised,
 *           and can remove any of them.
 *
 * Contents :
 *
 * * function(fullName) : an other name as one line
 * * function(OtherNamesCard) : the card, with a Remove button per name
 */

import { Icon } from "@iconify/react";
import { useQuery } from "@tanstack/react-query";

import { otherNamesQuery, useRemoveOtherName } from "@/portals/patient/api/patientQueries";
import type { OtherName } from "@/portals/patient/api/reviewItemsApi";
import { Button } from "@/shared/components/button";
import { toastError, toastSuccess } from "@/shared/lib/toast";
import { cn } from "@/shared/lib/utils";


// function(fullName)
/**
 * An other name as one line ("Toyosi O Morgan").
 *
 * @param name - The other name.
 * @returns Its parts, in order.
 */
export function fullName(name: OtherName): string {
  return [name.givenName, name.middleName, name.familyName].filter(Boolean).join(" ");
}


// function(OtherNamesCard)
/**
 * The profile's "Other names" card.
 *
 * @param className - Extra classes for the card.
 * @returns The card.
 */
export function OtherNamesCard({ className }: { className?: string }) {
  const { data, isLoading } = useQuery(otherNamesQuery(undefined));
  const names: OtherName[] = data ?? [];
  const remove = useRemoveOtherName(undefined);

  const onRemove = async (name: OtherName) => {
    try {
      await remove.mutateAsync(name.id);
      toastSuccess(`“${fullName(name)}” was removed. Uploads with this name will be checked with you again.`);
    } catch {
      toastError("Couldn't remove this name. Please try again.");
    }
  };

  return (
    <section className={cn("rounded-lg border border-[#E5E7EB] bg-white p-6", className)} data-testid="other-names">
      <h2 className="!mb-1 text-base font-bold text-[#111827]">Other names</h2>
      <p className="!mb-4 text-sm text-[#6B7280]">
        Names you confirmed as yours on an upload. A later upload with one of these names and your date of birth is
        added to your record without asking you again.
      </p>

      {isLoading ? null : names.length === 0 ? (
        <p className="!mb-0 text-sm text-[#9CA3AF]">None yet. They're added when you confirm an upload is yours.</p>
      ) : (
        <ul className="!mb-0 grid gap-2 !pl-0">
          {names.map((name) => (
            <li
              key={name.id}
              data-testid="other-name"
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#E5E7EB] px-4 py-3"
            >
              <div className="flex min-w-0 items-center gap-3">
                <Icon icon="lucide:id-card" className="h-4 w-4 shrink-0 text-[#6B7280]" />
                <div className="min-w-0">
                  <p className="!mb-0 truncate text-sm font-semibold text-[#111827]">{fullName(name)}</p>
                  {name.sourceLabel && (
                    <p className="!mb-0 truncate text-xs text-[#6B7280]">Confirmed on “{name.sourceLabel}”</p>
                  )}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={() => void onRemove(name)}
                loading={remove.isPending && remove.variables === name.id}
                className="!h-8 !rounded-lg !border-[#E5E7EB] !px-3 !text-xs !font-semibold !text-[#0A2947]"
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
